import { EventContextProvider, useAnalytics } from "@codat/analytics";
import {
  ArrowDownIcon,
  FocusTrap,
  IndicatorLabel,
  Menu,
  Popover,
} from "@codat/orchard-ui";
import { OrchardTheme } from "@codat/orchard-ui/dist/es/ThemeProvider/types";
import React, { useMemo, useState } from "react";
import { classNames } from "react-cmdk";
import { objectKeys } from "utils/typescript";

import Button from "components/Button";

import { ConnectionStatus, ConnectionStatusFilterState } from "../Companies";
import styles from "./ConnectionStatusDropdown.module.scss";

interface ConnectionStatusDropdownProps {
  checkedState: ConnectionStatusFilterState;
  onCheck: (state: ConnectionStatusFilterState) => void;
}

interface ChecklistProps extends ConnectionStatusDropdownProps {
  checkedCount: number;
}

const options: {
  status: ConnectionStatus;
  color: keyof OrchardTheme | "interface";
  label: string;
}[] = [
  { status: "Linked", color: "positive", label: "Linked" },
  { status: "PendingAuth", color: "action", label: "Pending auth" },
  { status: "Deauthorized", color: "warning", label: "Deauthorized" },
  { status: "Unlinked", color: "interface", label: "Unlinked" },
];

const Checklist = ({ onCheck, checkedState, checkedCount }: ChecklistProps) => {
  const { sendEvent } = useAnalytics();
  const headerCheckboxState = useMemo(() => {
    switch (checkedCount) {
      case 0:
        return false;
      case options.length:
        return true;
      default:
        return "indeterminate";
    }
  }, [checkedCount]);

  return (
    <>
      <Menu.Title
        title="Connection status"
        count={`${Object.values(checkedState).filter(Boolean).length}/${
          options.length
        }`}
        onCheck={() =>
          onCheck(
            objectKeys(checkedState).reduce((acc, key) => {
              return { ...acc, [key]: checkedCount !== options.length };
            }, checkedState)
          )
        }
        checked={headerCheckboxState}
      />
      <Menu.List
        data-testid="connection-status-filter-list"
        className={styles.list}
      >
        {options.map(({ status, color, label }) => {
          const itemCheckedState = !!checkedState[status];
          const handleCheck = () => {
            sendEvent("Checkbox - Check", {
              status,
              label,
              checked: !itemCheckedState,
            });
            onCheck({
              ...checkedState,
              [status]: !itemCheckedState,
            });
          };
          return (
            <Menu.Item
              key={status}
              className={styles.item}
              onClick={handleCheck}
              id={status}
              label={
                <IndicatorLabel
                  color={color}
                  label={label}
                  className={styles.label}
                />
              }
              checked={itemCheckedState}
              onCheck={handleCheck}
            />
          );
        })}
      </Menu.List>
    </>
  );
};

export const ConnectionStatusDropdown = ({
  onCheck,
  checkedState,
}: ConnectionStatusDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const checkedCount = useMemo(
    () => Object.values(checkedState).filter(Boolean).length,
    [checkedState]
  );

  return (
    <Popover
      isOpen={isOpen}
      content={
        <EventContextProvider name="Connection status filter">
          <FocusTrap>
            <Checklist
              onCheck={onCheck}
              checkedState={checkedState}
              checkedCount={checkedCount}
            />
          </FocusTrap>
        </EventContextProvider>
      }
      popoverClassName={styles.popover}
      onClose={() => setIsOpen(false)}
      closeOnEscape
      removePadding
      placement="bottom-start"
    >
      <Button
        color="interface"
        variant="secondary"
        label="Connection status"
        onClick={() => setIsOpen(!isOpen)}
        buttonName="Connection Status"
        data-testid="connection-status-filter-button"
      >
        <span className={styles.buttonChildren}>
          <span className={styles.buttonText}>Connection status</span>
          <span className={styles.buttonCount}>
            {options.length > checkedCount ? checkedCount : "All"}
          </span>
          <ArrowDownIcon className={classNames(isOpen && styles.arrowUp)} />
        </span>
      </Button>
    </Popover>
  );
};
